// ---------------------------------------------------------------------------
// resolveRound — one round of a war, both players' orders in, the world out
// ---------------------------------------------------------------------------
//
// The rules half of what useGameState.computeAttacks did inside React, lifted
// so a bot (or a simulation, or a test) can run a round with no React and no
// Firebase. The steps and their order are the war's, unchanged:
//
//   1. migrateLegacyStamps (letterless intel from old saves gets its letters);
//      purgeDestroyedRockets (rockets an older save left on destroyed cells)
//   2. declared broods stamp onto the Grids (a failed stamp is remembered)
//   3. ageDroneScans (last round's scan frames fade)
//   4. syncBaseLedger (each corridor's brood ledger re-read off its Grid)
//   5. resolveAllBattles, corridor 1 → 7; after each corridor: fired broods
//      retire from the Grid, the corridor's crater lands (craterSeed →
//      aimSubplot → resolveCrater), eggs hatch or die (hatchBroods), kills
//      count (applyKills), and both ledgers re-sync so the next corridor
//      fights against the damage
//   6. airdrops: dropZoneSources, the eggs that can still fly (standing
//      broods only — a fired brood's egg dies with it), deliveries
//   7. reconcileBaseDeaths (dead broods drop; a failed stamp refunds its pair)
//   8. the Grids are cloned, dead broods swept, surviving broods matured
//   9. drone scans land
//  10. yield: every producing lot on the post-resolution Hive pays (its
//      network's extractor multiplies it); the per-type tally grows
//  11. warOutcome
//
// Inputs are never mutated: every board and Grid is cloned on entry. A Grid
// may be passed as the JSON string Firebase holds or as an object; either way
// the order of `homefront.structures` keys is kept exactly as parsed, because
// that order decides which structure's rockets a partly-spent interception
// uses (subplotRockets.coveringStructures).

import { BATTLEFIELD_COUNT } from './gameConfig.js'
import { rules, withRules, RULES_VERSION } from './config.js'
import {
    resolveAllBattles,
    basesOf,
    dropZoneSources,
    applyResupplyDeliveries,
    refundFailedBaseBuild,
} from './gameLogic.js'
import { craterSeed, migrateLegacyStamps } from './subplotDamage.js'
import { resolveCrater, purgeDestroyedRockets } from './subplotRockets.js'
import { applyKills, warOutcome } from './structureKills.js'
import { tickYield } from './subplotEconomy.js'
import { cloneHomefront, migrateBroods } from './subplotGrid.js'
import { applyDroneScan, ageDroneScans } from './subplotSurveillance.js'
import {
    addBrood,
    removeBroodById,
    matureBroodAt,
    broodsFor,
    isBroodDead,
    sweepDeadBroods,
    hatchBroods,
    broodStrength,
    eggLaunchable,
} from './subplotBases.js'
import { aimSubplot, interceptedShots } from './strike.js'

const cloneJson = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)))
// Hives saved before rules v8 still hold their Broods among the structures.
const parseHf = (raw) => (raw == null || raw === '' ? null : migrateBroods(typeof raw === 'string' ? JSON.parse(raw) : cloneJson(raw)))
const parseArr = (raw, fallback) => (raw == null || raw === '' ? fallback : typeof raw === 'string' ? JSON.parse(raw) : cloneJson(raw))
const noTargets = () => Array(BATTLEFIELD_COUNT).fill(null)

// A player's orders and state, normalised and cloned.
function readPlayer(p) {
    if (!p?.board) throw new Error('resolveRound: each player needs a board')
    return {
        board: parseArr(p.board),
        homefront: parseHf(p.homefront),
        targetPlots: parseArr(p.targetPlots, noTargets()),
        targetSubplots: parseArr(p.targetSubplots, noTargets()),
        dropZones: parseArr(p.dropZones, []),
        baseBuilds: parseArr(p.baseBuilds, []),
        droneStrikes: parseArr(p.droneStrikes, []),
        structuresLost: p.structuresLost ?? 0,
        yield: p.yield ?? 0,
        yieldProduced: parseArr(p.yieldProduced, {}),
    }
}

// ── Brood ledger ↔ Grid ─────────────────────────────────────────────────────

// A brood's ledger entry mirrors its body on the Hive: hp is its live drones
// (broodStrength — its live outer cells), egg its nest's state
// (undefined until it matures; then 'egg' / 'gone'; 'hatched' on old Grids).
function mirrorBrood(hf, st, b) {
    const hp = broodStrength(hf, st)
    const egg = st.egg ?? (b.mature ? 'egg' : undefined)
    return (hp === (b.hp ?? rules().fortStrength) && egg === b.egg) ? b : { ...b, hp, ...(egg ? { egg } : {}) }
}

// Crater damage feeds back into the corridor ledgers IMMEDIATELY, so the next
// corridor litigates against it. Only broods the Grid actually holds
// (standing, or as this round's fresh corpse) are touched; a ledger brood with
// NO body on the Hive at all is left for reconcileBaseDeaths.
export function syncBaseLedger(hf, board) {
    if (!hf) return board
    return board.map((bf, col) => {
        const bases = basesOf(bf)
        if (!bases.length) return bf
        const liveByCenter = new Map(broodsFor(hf, col).map(st => [st.footprint[0], st]))
        const deadCenters = new Set()
        for (const id of Object.keys(hf.broods ?? {})) {
            const b = hf.broods[id]
            if (b.corridor === col && isBroodDead(hf, id)) deadCenters.add(b.footprint[0])
        }
        const survivors = bases
            .filter(b => liveByCenter.has(b.centerS) || !deadCenters.has(b.centerS))
            .map(b => (liveByCenter.has(b.centerS) ? mirrorBrood(hf, liveByCenter.get(b.centerS), b) : b))
        const changed = survivors.length !== bases.length ||
            survivors.some((b, i) => b !== bases[i])
        if (!changed) return bf
        return {
            ...bf,
            bases: survivors,
            fortCount: survivors.filter(b => b.mature).length,
            fortInConstruction: survivors.filter(b => !b.mature).length,
        }
    })
}

// A brood that lost its LAST cell dies on the corridor side too. A ledger
// brood with NO body on the Hive that was declared THIS round is different: its
// stamp failed, so its 2 drones come back. Any other ledgered brood missing its
// body on the Hive still drops (self-heal), with no refund.
export function reconcileBaseDeaths(hf, board, failedBuilds = []) {
    if (!hf) return board
    return board.map((bf, col) => {
        const bases = basesOf(bf)
        if (!bases.length) return bf
        const liveByCenter = new Map(broodsFor(hf, col).map(st => [st.footprint[0], st]))
        const survivors = bases
            .filter(b => liveByCenter.has(b.centerS))
            .map(b => mirrorBrood(hf, liveByCenter.get(b.centerS), b))
        const failedCenters = new Set(
            failedBuilds.filter(f => f.col === col).map(f => f.centerS)
        )
        const refunds = bases.filter(b =>
            !liveByCenter.has(b.centerS) && failedCenters.has(b.centerS)
        ).length
        const changed = survivors.length !== bases.length ||
            survivors.some((b, i) => b !== bases[i])
        if (!changed && refunds === 0) return bf
        let next = {
            ...bf,
            bases: survivors,
            fortCount: survivors.filter(b => b.mature).length,
            fortInConstruction: survivors.filter(b => !b.mature).length,
        }
        for (let i = 0; i < refunds; i++) next = refundFailedBaseBuild(next)
        return next
    })
}

// ── The round, from one side's point of view ────────────────────────────────
//
// `self` is the perspective player. Events come out in self's terms (WIN is a
// self win), exactly as each client has always computed them; the outcome is
// the same world from either side.
function resolveFromSide(self, opp, round, observe) {
    let selfHomefront = self.homefront
    let oppHomefront = opp.homefront
    // Wars saved before intel tagging carry letterless stamps — letter them
    // before this round mints any new letters.
    if (selfHomefront) migrateLegacyStamps(selfHomefront)
    if (oppHomefront) migrateLegacyStamps(oppHomefront)
    // Grids resolved before rules v7 can hold rockets on destroyed cells;
    // they are gone (a rocket goes with its cell).
    if (selfHomefront) purgeDestroyedRockets(selfHomefront)
    if (oppHomefront) purgeDestroyedRockets(oppHomefront)
    let selfLostAfter = self.structuresLost
    let oppLostAfter = opp.structuresLost

    // Broods: this round's declared builds stamp onto the Grids BEFORE the
    // craters — a fresh brood is hittable by this round's craters. Returns the
    // builds whose stamp FAILED (refunded by reconcileBaseDeaths).
    const applyDeclaredBases = (hf, builds) => {
        const failed = []
        if (!hf) return failed
        for (const b of builds) {
            if (b?.centerS == null || b.col == null) continue
            if (!addBrood(hf, b.col, b.centerS, round)) failed.push(b)
        }
        return failed
    }
    const selfFailedBuilds = applyDeclaredBases(selfHomefront, self.baseBuilds)
    const oppFailedBuilds = applyDeclaredBases(oppHomefront, opp.baseBuilds)

    // Last round's drone-scan frames age out BEFORE this round's scans land.
    if (selfHomefront) selfHomefront = ageDroneScans(selfHomefront)
    if (oppHomefront) oppHomefront = ageDroneScans(oppHomefront)

    // A fired brood retires from the Hive when ITS corridor clears — until
    // then any earlier corridor's crater can hit it.
    const selfFiredBases = []
    const oppFiredBases = []
    const retireFiredBases = (hf, bf, col, fired) => {
        if (!hf) return
        const wanted = new Set(basesOf(bf).map(b => b.centerS))
        for (const st of broodsFor(hf, col)) {
            if (wanted.has(st.footprint[0])) continue
            fired.push({ col, st })
            removeBroodById(hf, st.id)
        }
    }

    // One corridor's crater (if its clash produced one), landed on the running
    // homefronts. Returns the rules half of the reveal event plus the crater's
    // intermediate stages (for an observer).
    const landCrater = (event, col) => {
        const base = { ...event, oppTargetIndex: opp.targetPlots[col] ?? null }

        // Self won → a crater lands on the opponent's homefront (chosen target,
        // or a seeded retaliation subplot when self won on defense untargeted).
        if (event.type === 'WIN' && oppHomefront) {
            const margin = event.selfStrength - event.oppStrength
            const isRetaliation = self.targetPlots[col] === null && event.context === 'opp_attacked'
            const targetRow = self.targetPlots[col] !== null ? self.targetPlots[col] : null
            if (margin > 0 && (targetRow !== null || isRetaliation)) {
                const seed = craterSeed(round, col)
                const aimS = aimSubplot(oppHomefront, col, targetRow, self.targetSubplots[col] ?? null, seed)
                const r = resolveCrater(oppHomefront, aimS, margin, { seed })
                oppHomefront = r.homefront
                // A cratered drone or nest kills the egg.
                hatchBroods(oppHomefront)
                oppLostAfter = applyKills(oppLostAfter, r.structureKills)
                return {
                    event: { ...base, aimS, kills: r.structureKills.length, killSide: 'opp', washedByRockets: r.washed,
                             absorbed: interceptedShots(margin, r.rocketsFired, r.washed),
                             destroyedStructures: r.destroyedStructures, destroyedBroods: r.destroyedBroods,
                             retaliationRow: isRetaliation ? Math.floor(aimS % 15 / 3) : null },
                    stageHomefronts: r.stageHomefronts,
                }
            }
        }
        // Self lost → a crater lands on self's homefront.
        if (event.type === 'LOSS' && selfHomefront) {
            const margin = event.oppStrength - event.selfStrength
            const isRetaliation = opp.targetPlots[col] === null && event.context === 'self_attacked'
            const targetRow = opp.targetPlots[col] !== null ? opp.targetPlots[col] : null
            if (margin > 0 && (targetRow !== null || isRetaliation)) {
                const seed = craterSeed(round, col)
                const aimS = aimSubplot(selfHomefront, col, targetRow, opp.targetSubplots[col] ?? null, seed)
                const r = resolveCrater(selfHomefront, aimS, margin, { seed })
                selfHomefront = r.homefront
                hatchBroods(selfHomefront)
                selfLostAfter = applyKills(selfLostAfter, r.structureKills)
                return {
                    event: { ...base, aimS, kills: r.structureKills.length, killSide: 'self', washedByRockets: r.washed,
                             absorbed: interceptedShots(margin, r.rocketsFired, r.washed),
                             destroyedStructures: r.destroyedStructures, destroyedBroods: r.destroyedBroods,
                             oppTargetIndex: opp.targetPlots[col] ?? (isRetaliation ? Math.floor(aimS % 15 / 3) : null),
                             retaliationRow: isRetaliation ? Math.floor(aimS % 15 / 3) : null },
                    stageHomefronts: r.stageHomefronts,
                }
            }
        }
        return { event: base, stageHomefronts: null }
    }

    // The corridors fight with ledgers that match their Grids AS THE ROUND
    // OPENS: a brood's hp is re-read from its body on the Hive here.
    const selfBoardAtOpen = syncBaseLedger(selfHomefront, self.board)
    const oppBoardAtOpen = syncBaseLedger(oppHomefront, opp.board)

    const events = []
    const { selfBoard: selfPostBattle, oppBoard: oppPostBattle } = resolveAllBattles(selfBoardAtOpen, oppBoardAtOpen, {
        afterCorridor: (col, event, selfBoards, oppBoards, { selfBf, oppBf }) => {
            retireFiredBases(selfHomefront, selfBoards[col], col, selfFiredBases)
            retireFiredBases(oppHomefront, oppBoards[col], col, oppFiredBases)
            const landed = landCrater(event, col)
            events[col] = landed.event
            // Display hook: the caller sees the running Grids at exactly this
            // moment (after this corridor's crater, before the next corridor)
            // and may add fields of its own to the event.
            if (observe?.afterCorridor) {
                const extra = observe.afterCorridor({
                    col, event: landed.event, stageHomefronts: landed.stageHomefronts,
                    selfHomefront, oppHomefront, selfFiredBases, oppFiredBases, selfBf, oppBf,
                })
                if (extra) events[col] = { ...landed.event, ...extra }
            }
            return {
                selfBoard: syncBaseLedger(selfHomefront, selfBoards),
                oppBoard: syncBaseLedger(oppHomefront, oppBoards),
            }
        },
    })

    // Airdrops land after the clashes. Each crate launches from the brood that
    // earned its zone, paired off the PRE-battle boards. An egg flies only if
    // its brood still stands with the egg intact: a brood that fired took its
    // egg with it (the egg dies with the brood), and so did one that died.
    const eggBlocked = (hf, sources) => {
        const out = new Set()
        for (const src of sources) {
            if (!src) continue
            const st = hf ? broodsFor(hf, src.col).find(x => x.footprint[0] === src.centerS) : null
            if (!st || !eggLaunchable(hf, st)) out.add(src.centerS)
        }
        return out
    }
    const selfSources = dropZoneSources(selfBoardAtOpen, self.dropZones)
    const oppSources = dropZoneSources(oppBoardAtOpen, opp.dropZones)
    const selfDelivery = applyResupplyDeliveries(selfPostBattle, self.dropZones, selfSources, eggBlocked(selfHomefront, selfSources))
    const oppDelivery = applyResupplyDeliveries(oppPostBattle, opp.dropZones, oppSources, eggBlocked(oppHomefront, oppSources))

    const selfBoardFinal = reconcileBaseDeaths(selfHomefront, selfDelivery.boardArr, selfFailedBuilds)
    const oppBoardFinal = reconcileBaseDeaths(oppHomefront, oppDelivery.boardArr, oppFailedBuilds)

    // Maturation lands only now, after every crater: a construction is
    // scaffolding for this round's craters too.
    const matureSurvivingBases = (hf, board) => {
        board.forEach((bf, col) => {
            for (const b of basesOf(bf)) {
                if (b.mature) matureBroodAt(hf, col, b.centerS)
            }
        })
    }
    // Sweep and mature CLONES: the running Grid is the last clash's snapshot.
    if (selfHomefront) {
        selfHomefront = cloneHomefront(selfHomefront)
        sweepDeadBroods(selfHomefront)
        matureSurvivingBases(selfHomefront, selfBoardFinal)
    }
    if (oppHomefront) {
        oppHomefront = cloneHomefront(oppHomefront)
        sweepDeadBroods(oppHomefront)
        matureSurvivingBases(oppHomefront, oppBoardFinal)
    }

    // Surveillance drones resolve AFTER the craters, so the intel reports this
    // round's survivors.
    const selfScans = []
    const oppScans = []
    for (const d of self.droneStrikes) {
        if (d?.targetS == null || !oppHomefront) continue
        const r = applyDroneScan(oppHomefront, d.targetS)
        oppHomefront = r.homefront
        selfScans.push({ launchS: d.launchS ?? null, targetS: d.targetS, cells: r.cells })
    }
    for (const d of opp.droneStrikes) {
        if (d?.targetS == null || !selfHomefront) continue
        const r = applyDroneScan(selfHomefront, d.targetS)
        selfHomefront = r.homefront
        oppScans.push({ launchS: d.launchS ?? null, targetS: d.targetS, cells: r.cells })
    }

    return {
        events,
        self: {
            board: selfBoardFinal, homefront: selfHomefront, structuresLost: selfLostAfter,
            boardAtOpen: selfBoardAtOpen, fired: selfFiredBases, failedBuilds: selfFailedBuilds,
            delivery: selfDelivery, scans: selfScans,
        },
        opp: {
            board: oppBoardFinal, homefront: oppHomefront, structuresLost: oppLostAfter,
            boardAtOpen: oppBoardAtOpen, fired: oppFiredBases, failedBuilds: oppFailedBuilds,
            delivery: oppDelivery, scans: oppScans,
        },
    }
}

// resolveRound({ p1, p2, round }, { perspective, observe })
//
//   p1, p2  { board, homefront, targetPlots, targetSubplots, dropZones,
//             baseBuilds, droneStrikes, structuresLost, yield }
//           (each order field may be the JSON string Firebase holds, an
//           array, or absent)
//   round   the crater seed's round — the war has always used the round
//           counter AFTER both players' submits incremented it (the first
//           resolution is round 2)
//
// Returns
//   p1, p2    the players after the round: board, homefront, structuresLost,
//             yield (credited from the post-resolution Hive), yieldProduced
//             (by lot type, this war so far), orders cleared
//   events    one per corridor, in the PERSPECTIVE player's terms (default p1)
//   outcome   { over, winner: 'p1' | 'p2' | null, draw }
//   detail    per player: { boardAtOpen, fired, failedBuilds, delivery,
//             scans, yieldIncrement, yieldByType } — what a display needs to play the round
//             back (scans are the ones that player's drones made)
//
// `observe.afterCorridor(ctx)` (optional) runs after each corridor's crater
// with the running Grids in the perspective player's terms; whatever it returns
// is merged into that corridor's event. The rules never read it back.
// `config` (optional) overrides DEFAULT_RULES numbers for this round
// (rules/config.js); the result carries the RULES_VERSION it ran under.
export function resolveRound(input, { perspective = 'p1', observe = null, config = null } = {}) {
    return withRules(config, () => resolveRoundWith(input, perspective, observe))
}

function resolveRoundWith({ p1, p2, round }, perspective, observe) {
    const P1 = readPlayer(p1)
    const P2 = readPlayer(p2)
    const selfIsP1 = perspective !== 'p2'
    const r = resolveFromSide(selfIsP1 ? P1 : P2, selfIsP1 ? P2 : P1, round, observe)
    const out1 = selfIsP1 ? r.self : r.opp
    const out2 = selfIsP1 ? r.opp : r.self

    // Yield: every lot still producing after the whole round pays.
    const t1 = tickYield(out1.homefront)
    const t2 = tickYield(out2.homefront)
    const y1 = t1.yieldIncrement
    const y2 = t2.yieldIncrement
    const tally = (prev, byType) => {
        const out = { ...prev }
        for (const [k, n] of Object.entries(byType)) out[k] = (out[k] ?? 0) + n
        return out
    }

    const finish = (input, out, yieldIncrement, byType) => ({
        board: out.board,
        homefront: out.homefront,
        structuresLost: out.structuresLost,
        yield: input.yield + yieldIncrement,
        // The yield this war has produced so far, by lot type (war end
        // converts the unspent yield in these proportions).
        yieldProduced: tally(input.yieldProduced, byType),
        targetPlots: noTargets(),
        targetSubplots: noTargets(),
        dropZones: [],
        baseBuilds: [],
        droneStrikes: [],
    })

    const o = warOutcome(out1.structuresLost, out2.structuresLost)
    const outcome = {
        over: o.over,
        winner: o.result === 'WIN' ? 'p1' : o.result === 'LOSS' ? 'p2' : null,
        draw: o.result === 'TIE',
    }

    const detailOf = (out, yieldIncrement, yieldByType) => ({
        boardAtOpen: out.boardAtOpen,
        fired: out.fired,
        failedBuilds: out.failedBuilds,
        delivery: out.delivery,
        scans: out.scans,
        yieldIncrement,
        yieldByType,
    })

    return {
        rulesVersion: RULES_VERSION,
        p1: finish(P1, out1, y1, t1.byType),
        p2: finish(P2, out2, y2, t2.byType),
        events: r.events,
        outcome,
        detail: { p1: detailOf(out1, y1, t1.byType), p2: detailOf(out2, y2, t2.byType) },
    }
}
